import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import BlockNodeView from './BlockNodeView';
import PageSectionView from './PageSectionView';
import InvestmentGroupView from './InvestmentGroupView';
import InvestmentHeaderView from './InvestmentHeaderView';
import InvestmentTotalView from './InvestmentTotalView';

/**
 * Creates a Tiptap Node extension for a block type.
 * All blocks share the same NodeView (BlockNodeView) and attrs structure.
 */
export function createBlockNode(name: string) {
  return Node.create({
    name,
    group: 'block',
    // Pagina's verplaats je in de paginalijst; slepen in het document verstoorde tekstselectie.
    draggable: false,
    atom: true,

    addAttributes() {
      return {
        id: { default: null },
        quoteId: { default: null },
        order: { default: 0 },
        content: { default: {} },
        styles: { default: {} },
        config: { default: {} },
      };
    },

    parseHTML() {
      return [{ tag: `div[data-block="${name}"]` }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-block': name }, 0];
    },

    addNodeView() {
      return ReactNodeViewRenderer(BlockNodeView) as any;
    },
  });
}

/**
 * Non-atom section node whose content is native Tiptap blocks (headings, paragraphs, lists).
 * The sheet pagination (sheetPagination.ts) can spread this node over several A4 sheets because it is NOT atom.
 */
export function createSectionNode(name: string) {
  return Node.create({
    name,
    group: 'block',
    draggable: false,
    atom: false,
    // Een pagina is een blok: Enter op een lege regel, Backspace aan het begin en
    // Opmaak wissen mogen tekst niet uit de pagina tillen of de pagina splitsen/samenvoegen.
    isolating: true,
    content: '(paragraph | heading | bulletList | orderedList | blockquote | image)+',

    addAttributes() {
      return {
        id: { default: null, keepOnSplit: true },
        quoteId: { default: null, keepOnSplit: true },
        order: { default: 0, keepOnSplit: true },
        sectionStyle: { default: 'default', keepOnSplit: true },
        /** ubo | ubx — per sectie; null = erf quote (inferQuotePageBrand). */
        theme: { default: null, keepOnSplit: true },
        clientLogoUrl: { default: null, keepOnSplit: true },
        agencyLogoUrl: { default: '/ubo/logo.svg', keepOnSplit: true },
        title: { default: '', keepOnSplit: true },
        showCompanyData: { default: false, keepOnSplit: true },
        companyName: { default: '', keepOnSplit: true },
        companyAddress: { default: '', keepOnSplit: true },
        companyCity: { default: '', keepOnSplit: true },
        companyZip: { default: '', keepOnSplit: true },
        styles: { default: {}, keepOnSplit: true },
        config: { default: {}, keepOnSplit: true },
      };
    },

    parseHTML() {
      return [{ tag: `div[data-section="${name}"]` }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-section': name }, 0];
    },

    addNodeView() {
      return ReactNodeViewRenderer(PageSectionView) as any;
    },
  });
}

/**
 * Investment group node: non-atom wrapper that holds investmentHeader + native tables + totals.
 * The sheet pagination (sheetPagination.ts) spreads the native table rows over A4 sheets.
 * Content pattern: one or more sequences of (header, table, total).
 */
export function createInvestmentGroupNode() {
  return Node.create({
    name: 'investmentGroupNode',
    group: 'block',
    draggable: false,
    atom: false,
    content: '(investmentHeaderNode | investmentTotalNode | table)+',

    addAttributes() {
      return {
        id: { default: null },
        quoteId: { default: null },
        order: { default: 0 },
        clientLogoUrl: { default: null },
        agencyLogoUrl: { default: '/ubo/logo.svg' },
        /** EUR | GBP | USD — zelfde als InvestmentBlockContent.currency */
        currency: { default: 'EUR' },
        /** nl | en — vaste tabellabels */
        labelLanguage: { default: 'nl' },
        /** ubo | ubx — per investment block; null = erf quote-brand. */
        theme: { default: null },
        /** Optionele tarieventabel (JSON-object); null = geen. */
        rateCard: { default: null },
        styles: { default: {} },
        config: { default: {} },
      };
    },

    parseHTML() {
      return [{ tag: 'div[data-investment-group]' }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-investment-group': '' }, 0];
    },

    addNodeView() {
      return ReactNodeViewRenderer(InvestmentGroupView) as any;
    },
  });
}

/**
 * Investment header node: atom node for phase title + config bar.
 * Contains the phase title, pricing type selector, and action buttons.
 */
export function createInvestmentHeaderNode() {
  return Node.create({
    name: 'investmentHeaderNode',
    draggable: false,
    atom: true,

    addAttributes() {
      return {
        mainTitle: { default: 'Investeringsoverzicht' },
        phaseTitle: { default: '' },
        pricingType: { default: 'fixed' },
        tableId: { default: null },
        isMainHeader: { default: true },
        clientLogoUrl: { default: null },
        agencyLogoUrl: { default: '/ubo/logo.svg' },
        /** Subtitel onder fasetitel (niet op hoofdheader). */
        phaseSubtitle: { default: '' },
        /** false = uurtarief- en urenkolom niet op de offerte (value based pricing). */
        showHourColumns: { default: true },
        /** false = alleen omschrijvingen en het totaal op de offerte, geen bedrag per regel. */
        showRowPrices: { default: true },
      };
    },

    parseHTML() {
      return [{ tag: 'div[data-investment-header]' }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-investment-header': '' }, 0];
    },

    addNodeView() {
      return ReactNodeViewRenderer(InvestmentHeaderView) as any;
    },
  });
}

/**
 * Investment total node: atom node that displays the total for a phase.
 * Rendered below each native table.
 */
export function createInvestmentTotalNode() {
  return Node.create({
    name: 'investmentTotalNode',
    draggable: false,
    atom: true,

    addAttributes() {
      return {
        tableId: { default: null },
        pricingType: { default: 'fixed' },
        label: { default: 'Totaal excl. BTW' },
        fixedTotal: { default: 0 },
        fixedTotalText: { default: null },
        showTotalRow: { default: true },
      };
    },

    parseHTML() {
      return [{ tag: 'div[data-investment-total]' }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-investment-total': '' }, 0];
    },

    addNodeView() {
      return ReactNodeViewRenderer(InvestmentTotalView) as any;
    },
  });
}
